export const HERO_ROLES = ["tank", "damage", "support"] as const;
export type HeroRole = (typeof HERO_ROLES)[number];

export const GAME_MODES = ["competitive", "quickplay"] as const;
export type GameMode = (typeof GAME_MODES)[number];

export const ROLE_FILTERS = ["all", ...HERO_ROLES] as const;
export type RoleFilter = (typeof ROLE_FILTERS)[number];

export const ROLE_LABELS: Record<HeroRole, string> = { tank: "Tank", damage: "Damage", support: "Support" };

/**
 * Hero subroles, grouped by role. Subroles are Blizzard's hero-specialization
 * taxonomy within each role (e.g. damage flankers, support tacticians).
 */
export const SUBROLES_BY_ROLE = {
    tank: ["initiator", "stalwart", "bruiser"],
    damage: ["flanker", "sharpshooter", "specialist", "recon"],
    support: ["tactician", "survivor", "medic"],
} as const satisfies Record<HeroRole, readonly string[]>;

export type TankSubrole = (typeof SUBROLES_BY_ROLE)["tank"][number];
export type DamageSubrole = (typeof SUBROLES_BY_ROLE)["damage"][number];
export type SupportSubrole = (typeof SUBROLES_BY_ROLE)["support"][number];
export type Subrole = TankSubrole | DamageSubrole | SupportSubrole;

/** Subrole display labels (title-cased). */
export const SUBROLE_LABELS: Record<Subrole, string> = {
    initiator: "Initiator",
    stalwart: "Stalwart",
    bruiser: "Bruiser",
    flanker: "Flanker",
    sharpshooter: "Sharpshooter",
    specialist: "Specialist",
    recon: "Recon",
    tactician: "Tactician",
    survivor: "Survivor",
    medic: "Medic",
};

/** Hero stats with an assigned tier label (computed client-side). */
export interface HeroStats {
    name: string;
    slug: string;
    role: HeroRole;
    /** Hero specialization within a role (e.g. "flanker"). Null if not yet surfaced by Blizzard. */
    subrole: string | null;
    winRate: number;
    pickRate: number;
    /**
     * Percent of matches the hero is banned (0–1).
     * `null` when no ban data has been recorded (legacy snapshots, QP).
     */
    banRate: number | null;
    tier: string;
    /** Composite z-score used for tier assignment (higher = stronger). */
    tierScore: number;
}

/** Raw hero stats as stored in the JSON (no tier yet). */
export interface RawHeroStats {
    name: string;
    slug: string;
    role: HeroRole;
    subrole: string | null;
    winRate: number;
    pickRate: number;
    banRate: number | null;
}

export type MapGameMode = "control" | "escort" | "hybrid" | "push" | "flashpoint" | "clash";

export interface MapInfo {
    key: string;
    name: string;
}

export interface TierInfo {
    key: string;
    name: string;
}

// ---------------------------------------------------------------------------
// Per-hero history (static/history/heroes/{slug}.json)
// ---------------------------------------------------------------------------

/**
 * Snapshot tuple for one (mode, tier) combination.
 *
 * - 2-element form `[winRate, pickRate]` is the legacy format from before ban
 *   rate was tracked. Readers interpret the missing 3rd element as `null` BR
 *   (unknown — distinct from a true 0% ban rate).
 * - 3-element form `[winRate, pickRate, banRate]` includes ban rate.
 *   `banRate` can be `0` (truly never banned), `-0.01` (Blizzard's
 *   insufficient-data sentinel), or `null` (unknown for this period).
 */
export type HistoryTuple = [number, number] | [number, number, number | null];

/** Stats for a single input/region combo within a history snapshot. */
export interface HeroHistoryComboData {
    /** Competitive tier → snapshot tuple */
    competitive: Record<string, HistoryTuple>;
    /** Quick Play snapshot tuple */
    quickplay: HistoryTuple;
}

/**
 * A daily snapshot of a hero's stats across all input/region combos.
 *
 * Keyed by combo string (e.g. "PC/Americas", "Console/Europe").
 * Legacy snapshots (pre-combo) stored `competitive` and `quickplay` at the
 * top level — the app handles both formats via fallback.
 */
export interface HeroHistorySnapshot {
    /** ISO date string YYYY-MM-DD */
    date: string;
    /** Combo-keyed data (new format) */
    [comboKey: string]: HeroHistoryComboData | string;
}

/** Shape of static/history/heroes/{slug}.json */
export interface HeroHistory {
    snapshots: HeroHistorySnapshot[];
}

// ---------------------------------------------------------------------------
// Chart types
// ---------------------------------------------------------------------------

import type { TrendDataPoint } from "./utils/stats";

/**
 * One series to display on a trend chart.
 * Each series represents a hero's trend line.
 */
export interface TrendSeries {
    name: string;
    color: string;
    data: TrendDataPoint[];
}

export type { TrendDataPoint };

// ---------------------------------------------------------------------------
// Current stats snapshot (src/lib/data/stats.json)
// ---------------------------------------------------------------------------

/** Shape of src/lib/data/stats.json */
export interface StatsData {
    fetchedAt: string;
    maps: MapInfo[];
    tiers: TierInfo[];
    data: {
        /** Competitive: tier → map → hero stats */
        competitive: Record<string, Record<string, RawHeroStats[]>>;
        /** Quick Play: map → hero stats (no tier breakdown) */
        quickplay: Record<string, RawHeroStats[]>;
    };
}

/**
 * Shape of static/history/maps/{combo}/{patchDate}.json — a frozen snapshot of a
 * combo's per-map stats (`StatsData["data"]`) at a balance patch.
 */
export type MapHistoryData = StatsData["data"];

// ---------------------------------------------------------------------------
// Summary data (static/history/summary-*.json)
// ---------------------------------------------------------------------------

/** A single hero's rate change for a mover card. */
export interface MoverEntry {
    slug: string;
    name: string;
    role: HeroRole;
    /** Signed delta (positive = increased, negative = decreased) */
    change: number;
}

/** One timeframe's mover data (e.g. "week", "month", "patch"). */
export interface MoverTimeframe {
    label: string;
    from: string;
    to: string;
    wrMovers: MoverEntry[];
    prMovers: MoverEntry[];
}

/** Shape of static/history/summary-*.json */
export interface SummaryData {
    timeframes: Record<string, MoverTimeframe>;
    /** Per-tier win-rate deltas since last significant patch. Outer key = tier name or "quickplay", inner = slug → signed delta. */
    patchTrends?: Record<string, Record<string, number>>;
    /** The patch date that patchTrends are measured from (the last "significant" balance patch). */
    patchDate?: string;
}

// ---------------------------------------------------------------------------
// Patches data (static/data/patches.json)
// ---------------------------------------------------------------------------

/** A single ability change within a hero's balance update. */
export interface PatchAbilityChange {
    ability: string;
    details: string[];
}

/** All balance changes for one hero in a patch. */
export interface PatchHeroChange {
    changes: PatchAbilityChange[];
    /** Developer commentary explaining the reasoning behind the changes. */
    devComment?: string;
}

/** A single patch scraped from Blizzard's patch notes. */
export interface PatchEntry {
    date: string;
    url: string;
    hasBalanceChanges: boolean;
    heroSlugs: string[];
    /** Detailed balance changes per hero (only present when hasBalanceChanges is true). */
    heroChanges?: Record<string, PatchHeroChange>;
}

/** Shape of static/data/patches.json */
export interface PatchesData {
    fetchedAt: string;
    patches: PatchEntry[];
}

// ---------------------------------------------------------------------------
// Patch trends (static/history/patch-trends-*.json)
// ---------------------------------------------------------------------------

export type { PatchHeroTrend, PatchTrendsData } from "./utils/tiers";
